import { BUILDINGS } from '../../data/buildings.js';
import { BULWARK_HUB_BUILD_SPEED_MULT } from '../../data/doctrines.js';
import type { MatchState } from '../State.js';
import { SECONDS_PER_TICK } from '../State.js';
import type { EntityId } from '../ids.js';

// BuildingSystem responsibilities, per tick:
//   1. Advance buildProgress for any under-construction building. On crossing 1.0
//      the building becomes operational; turrets begin firing in CombatSystem,
//      hubs accept ProduceSquadCommand in ProductionSystem.
//   2. Reap buildings whose hp reached 0 (turrets/hubs can be killed).
//
// Per docs/prompts/04_production_buildings.md. Validation of BuildCommand (cost,
// slot empty, ownership, ≤2 hubs/side) lives in Simulation.applyCommand so the
// system can stay O(N) over buildings without re-checking ownership per tick.
//
// V1.7 (2.12 design fix): Hubs ADD a parallel production queue rather than
// taking over the Core's. The earlier "Hub takeover" rule was confusing in
// playtests — players read the dimmed Core panel as a broken state and lost
// any signal sunk into the Core queue on Hub-completion. With ADD semantics
// each Hub is an unambiguous +1 queue (0 Hubs → 1 queue at Core; 1 Hub → 2
// queues; 2 Hubs → 3 queues; cap at MAX_HUBS_PER_PLAYER = 2). Core's
// `produces` is always non-null for a live Core.
export function buildingSystem(state: MatchState): void {
  advanceBuildProgress(state);
  reapDeadBuildings(state);
}

function advanceBuildProgress(state: MatchState): void {
  for (const b of Object.values(state.buildings)) {
    if (b.buildProgress >= 1) continue;
    const def = BUILDINGS[b.kind];
    // V1.5 Bulwark: production_hub builds 10% faster for a Bulwark owner.
    // Turret/mini-turret build speed is unchanged (Bulwark's turret bonus is
    // range, not build time — see CombatSystem.turretRangeMultFor).
    const speedMult =
      b.kind === 'production_hub' && state.players[b.ownerId].doctrineId === 'bulwark'
        ? BULWARK_HUB_BUILD_SPEED_MULT
        : 1;
    b.buildProgress = Math.min(1, b.buildProgress + (SECONDS_PER_TICK * speedMult) / def.buildTimeSec);
  }
}

function reapDeadBuildings(state: MatchState): void {
  const dead: EntityId[] = [];
  for (const b of Object.values(state.buildings)) {
    if (b.hp <= 0) dead.push(b.id);
  }
  for (const id of dead) delete state.buildings[id];
}
